import React from "react";
import {
  Image,
  StyleSheet,
  Text,
  TouchableNativeFeedback,
  TouchableOpacity,
  View,
} from "react-native";
import dayjs from "dayjs";
import CustomText from "@components/CustomText";
import { useNavigation } from "@react-navigation/native";
import { CrateV2, Delivered, Order, PacketsV2, CustomersV2, ItemsV2 } from "@assets/svg/MyDeliveries";
import Rupees from "@assets/svg/RupeesIcon";
import RightArrow from "@assets/svg/RightArrow";
import { colors } from "@constants/theme";
import color from "@constants/color";
import t from "@constants/localization";
import analytics from "@services/analytics";
import {
  MY_DELIVERY_EVENTS,
  DELIVERY_CODE_EVENTS,
} from "@constants/analyticsEventsCT";
import { fontFamily } from "@utils/styles";
import Info from "@assets/svg/MyDeliveries/Info";
import { getTodaysCrateDeliveries } from "@services/api";
import { useSelector } from "react-redux";
import { getUnableToDeliverText, getCrateListFromCrateIds } from "./utils";
import DeliveryBoyInfoComponent from "./DeliveryBoyInfoComponent";
import CratePacketItemsInfo from "../components/CratePacketItemsInfo";

const PATH = "screens/MyDeliveries/DeliveryReturn/DeliveryCard";

function DeliveryCard({
  deliveryDetails,
  deliveryBoyInfo,
  spokeName,
  deliveriesData,
  setModalVisible,
  setInfoTooltipVisible,
  setInfoTooltipData,
  setSpokeDeliveryReturnDetails,
}) {
  const { isSortingEnabled = false, features = {} } = useSelector(
    ({ configReducer }) => configReducer,
  );
  const user = useSelector(({ auth }) => auth?.user);
  const isCpDb = user?.cp_db_id;

  const navigation = useNavigation();
  const deliveryState = deliveryDetails?.state;
  const Content = [
    {
      field: "crate",
      icon: <CrateV2 />,
      text: t(`${PATH}/crate`),
      value: deliveryDetails?.noOfCrates || 0,
      list: deliveryDetails?.noOfCrates
        ? getCrateListFromCrateIds(deliveryDetails.crates_list)
        : [],
    },
    {
      field: "packets",
      icon: <PacketsV2 />,
      text: t(`${PATH}/packets`),
      value: deliveryDetails?.noOfShipments || 0,
      list: deliveryDetails?.noOfShipments
        ? deliveryDetails.user_shipments_list
        : [],
    },
    {
      field: "hwi",
      icon: <ItemsV2 />,
      text: t(`${PATH}/items`),
      value: deliveryDetails?.noOfOpenItems || 0,
      list: deliveryDetails?.noOfOpenItems
        ? deliveryDetails.user_open_items_list
        : [],
    },
    {
      field: "customer",
      icon: <CustomersV2 />,
      text: t(`${PATH}/customers`),
      value: deliveryDetails?.noOfUsers,
    },
  ];

  const handleDeliveryCardPress = () => {
    navigation.navigate(
      deliveryBoyInfo.status === "ASSIGNED" && (deliveryDetails?.noOfCrates > 0 || deliveryDetails?.noOfOpenItems > 0)
        ? isSortingEnabled
          ? "SortingDeliveries"
          : "DeliveryOrdersCrateList"
        : "DeliveryOrdersList",
      {
        date: deliveriesData?.date || dayjs().format("YYYY-MM-DD"),
        spokeName,
        tab: "Current day",
        type: deliveryBoyInfo?.status,
      },
    );
    analytics.trackEvent(MY_DELIVERY_EVENTS.VIEW_DETAILS_CLICKED, {
      screen: "Delivery Page",
      tab: "Current day",
      section: "Deliveries",
    });
  };

  const handleNotDeliveredPress = () => {
    setModalVisible(false);
    navigation.navigate("DeliveryOrdersList", {
      date: deliveriesData?.date || dayjs().format("YYYY-MM-DD"),
      spokeName,
      tab: "Current day",
      forNotDelivered: true,
    });

    analytics.trackEvent(MY_DELIVERY_EVENTS.VIEW_DETAILS_CLICKED, {
      screen: "Delivery Page",
      tab: "Current day",
      section: "Deliveries",
      forNotDelivered: true,
    });
  };

  const DeliveryCodeButtonComponent = () => {
    return (
      <TouchableOpacity
        testID="see_delivery_code"
        onPress={() => onPressDeliveryCode(spokeName)}
      >
        <View style={styles.otpContainer}>
          <Text style={styles.otpText}>डिलीवरी कोड देखें</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const onPressDeliveryCode = async (spoke) => {
    try {
      if (deliveryBoyInfo.db_status === "NOT_GENERATED") {
        const { data = {} } = await getTodaysCrateDeliveries();
        const spokeDetails = data.spokeDetails.find(
          (sd) => sd.spokeName === spoke,
        );
        const { db_status } = spokeDetails?.deliveryDetails?.deliveryBoyInfo;
        setSpokeDeliveryReturnDetails(spokeDetails);
        if (db_status === "NOT_GENERATED") {
          setModalVisible(true);
        } else {
          analytics.trackEvent(DELIVERY_CODE_EVENTS.VIEW_CODE, {
            mode: "GENERATED_VIA_API",
            spoke,
          });
          return navigation.navigate("DeliveryReturnOverview", {
            spokeDetails,
            routeType: deliveryBoyInfo?.route_type,
          });
        }
      } else {
        const spokeDetails = deliveriesData.spokeDetails.find(
          (sd) => sd.spokeName === spoke,
        );
        setSpokeDeliveryReturnDetails(spokeDetails);
        analytics.trackEvent(DELIVERY_CODE_EVENTS.VIEW_CODE, {
          mode: "ALREADY_GENERATED",
          spoke,
        });
        return navigation.navigate("DeliveryReturnOverview", {
          spokeDetails,
          routeType: deliveryBoyInfo?.route_type,
        });
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  const onPressInfo = (data) => {
    setInfoTooltipVisible(true);
    setInfoTooltipData(data);
  };
  const cpDbCheck = (Component) => {
    return isCpDb ? null : <Component />;
  };
  return (
    <View style={styles.cardContainer}>
      <TouchableNativeFeedback
        testID="handleDeliveryCardPress"
        background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
        onPress={() => handleDeliveryCardPress()}
      >
        <View style={styles.card}>
          <View
            style={[
              styles.headerContainer,
              {
                backgroundColor:
                  deliveryState === "Delivery"
                    ? "rgba(0, 171, 89, 0.04)"
                    : "rgba(0, 0, 0, 0.04)",
                flex: 1,
                justifyContent: "space-between",
              },
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Delivered status={deliveryState} />
              <Text
                style={
                  deliveryState === "Delivery"
                    ? styles.greenHeaderText
                    : styles.greyHeaderText
                }
              >
                {t(`${PATH}/${deliveryState}`)}
              </Text>
            </View>

            {deliveryDetails.state !== "Delivered" &&
              deliveryBoyInfo.status !== "NOT_ASSIGNED"
              ? cpDbCheck(DeliveryCodeButtonComponent)
              : null}
          </View>
          {deliveryDetails.state === "Delivery" ? (
            cpDbCheck(() => (
              <DeliveryBoyInfoComponent deliveryBoyInfo={deliveryBoyInfo} />
            ))
          ) : (
            <View>
              {deliveryDetails?.usersUnableToDeliver ? (
                <TouchableOpacity
                  testID="handleNotDeliveredPress"
                  onPress={handleNotDeliveredPress}
                  style={styles.notDeliveredContainer}
                >
                  <View style={styles.notDeliveredBody}>
                    <View style={{ flexDirection: "row" }}>
                      <Image
                        resizeMode="contain"
                        style={styles.notDeliveredIcon}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/ss%2F1681745664208-CX%20RTO.png",
                        }}
                      />
                      <View style={styles.notDeliveredTextContainer}>
                        <Text style={styles.notDeliveredTitle}>
                          {getUnableToDeliverText(
                            deliveryDetails?.unableToDeliver,
                          )}
                        </Text>
                        <Text style={styles.notDeliveredCustomer}>
                          {t(`${PATH}/customerEffect`, {
                            count: deliveryDetails?.usersUnableToDeliver,
                          })}
                        </Text>
                      </View>
                    </View>
                    <RightArrow
                      stroke={colors.extraLightBlack}
                      fill={colors.extraLightBlack}
                      style={styles.notDeliveredRightIcon}
                    />
                  </View>
                  <Text style={styles.notDeliveredDescription}>
                    {t(`${PATH}/viewDetailsToTake`)}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          )}
          <CratePacketItemsInfo Content={Content} onPressInfo={onPressInfo} />
          <View style={styles.buttonStyle}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              {features?.overview_delivered_amount ? (
                <>
                  <Rupees
                    bgColor="#00AB59"
                    bgOpacity={1}
                    color="rgba(255, 255, 255, 1)"
                    width={20}
                    height={20}
                  />
                  <CustomText
                    variant="body_text_3"
                    style={{
                      color: color.lightBlack,
                      marginLeft: 8,
                      marginRight: 4,
                    }}
                  >
                    कैश पेमेंट :
                  </CustomText>
                  <CustomText
                    variant="body_text_1"
                    style={styles.amountColor}
                  >
                    {t(`${PATH}/rs`)}
                  </CustomText>
                  <CustomText variant="title_16" style={styles.amountColor}>
                    {`${deliveryDetails.totalPayableAmount}`}
                  </CustomText>
                </>
              ) : null}
            </View>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={styles.viewDetailsText}>
                {t(`${PATH}/viewDetails`)}
              </Text>
              <RightArrow
                stroke={colors.extraLightBlack}
                fill={colors.extraLightBlack}
              />
            </View>
          </View>
        </View>
      </TouchableNativeFeedback>
    </View>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 8,
  },
  card: {
    backgroundColor: colors.white,
    borderWidth: 0.5,
    borderRadius: 10,
    elevation: 2,
    borderColor: colors.lighterBlack,
  },
  headerContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  greenHeaderText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.plain_green,
    marginHorizontal: 4,
  },
  greyHeaderText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.lightBlack,
    alignItems: "center",
    marginHorizontal: 4,
  },
  detailsOuterCard: {
    flexDirection: "row",
    paddingVertical: 8,
    flexWrap: 'wrap'
  },
  detailsInnercard: {
    flexDirection: "row",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    marginTop: 0,
    margin: 8,
    padding: 6,
    borderRadius: 12,
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",

  },
  cardCount: {
    marginTop: 3,
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  buttonStyle: {
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderTopWidth: 0.8,
    borderColor: colors.lighterBlack,
    width: "100%",
  },
  detailsCard: {
    flexDirection: "row",
    width: '50%',
  },
  separator: {
    width: 1,
    height: 24,
    backgroundColor: colors.lighterBlack,
  },
  viewDetailsText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    lineHeight: 20,
    marginRight: 8,
  },
  contentContainer: {
    marginHorizontal: 6,
  },
  otpContainer: {
    backgroundColor: colors.plain_green,
    paddingHorizontal: 10,
    borderRadius: 40,
    paddingVertical: 2,
  },
  otpText: {
    fontSize: 11,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.white,
    lineHeight: 18,
  },
  notDeliveredContainer: {
    borderColor: "rgba(255, 46, 0, 0.2)",
    backgroundColor: "#FFEEEB",
    margin: 12,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingTop: 11,
    paddingBottom: 8,
  },
  notDeliveredIcon: {
    width: 38,
    height: 35,
    marginTop: 2,
  },
  notDeliveredTextContainer: {
    marginStart: 12,
  },
  notDeliveredRightIcon: {
    alignSelf: "center",
  },
  notDeliveredTitle: {
    fontSize: 14,
    fontFamily: fontFamily.bold,
    color: colors.plain_red,
  },
  notDeliveredDescription: {
    fontSize: 12,
    fontFamily: fontFamily.regular,
    color: colors.mediumBlack,
  },
  notDeliveredCustomer: {
    fontSize: 14,
    fontFamily: fontFamily.semiBold,
    color: colors.mediumBlack,
  },
  notDeliveredBody: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  amountColor: {
    color: "rgba(0, 0, 0, 0.86)",
  },
  title: {
    marginLeft: 8,
    marginBottom: 4,
    fontFamily: fontFamily.bold,
    fontSize: 16,
    color: colors.black06,
  },
  value: {
    fontSize: 18,
    fontFamily: fontFamily.bold,
    color: colors.mediumBlack,
    marginRight: 6
  },
});

export default DeliveryCard;
